import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type {
  ModelShowPricing,
  ShowSummary,
  ShowType,
  ShowTypeInfo,
  TransitionShowRequest,
} from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { isPublicMainStage, listModelTransitionTargets, TICKET_DURATION_OPTIONS } from '@fetmode/utils';
import { Button } from '@fetmode/ui';

export interface ShowLiveManagementPanelProps {
  liveShow: ShowSummary;
  isSubmitting: boolean;
  onShowUpdated: (show: ShowSummary) => void;
  onError: (message: string) => void;
}

function toLocalDateTimeValue(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function ShowLiveManagementPanel({
  liveShow,
  isSubmitting,
  onShowUpdated,
  onError,
}: ShowLiveManagementPanelProps): ReactElement {
  const { api } = useAuth();
  const [showTypes, setShowTypes] = useState<ShowTypeInfo[]>([]);
  const [pricing, setPricing] = useState<ModelShowPricing | null>(null);
  const [scheduledAt, setScheduledAt] = useState(() =>
    toLocalDateTimeValue(new Date(Date.now() + 60 * 60_000)),
  );
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [ticketCapacity, setTicketCapacity] = useState('');
  const [entryFeeCoins, setEntryFeeCoins] = useState('');
  const [minParticipants, setMinParticipants] = useState('2');
  const [coinsPerMinute, setCoinsPerMinute] = useState('');

  useEffect(() => {
    void api.listShowTypes().then((result) => {
      setShowTypes(result.types);
    });
    void api.getModelShowPricing().then((result) => {
      setPricing(result.pricing);
      setDurationMinutes(
        (TICKET_DURATION_OPTIONS as readonly number[]).includes(result.pricing.ticketDurationMinutes)
          ? result.pricing.ticketDurationMinutes
          : 60,
      );
      setEntryFeeCoins(String(result.pricing.partyEntryCoins));
      setMinParticipants(String(result.pricing.partyMinParticipants));
      setCoinsPerMinute(
        String(
          Math.max(
            1,
            Math.floor(
              (result.pricing.vipCoinsPerMinute * result.pricing.spyDiscountPercent) / 100,
            ),
          ),
        ),
      );
      if (result.pricing.ticketCapacity !== null) {
        setTicketCapacity(String(result.pricing.ticketCapacity));
      }
    });
  }, [api]);

  function buildTransitionBody(toType: ShowType): TransitionShowRequest | null {
    if (toType === 'TICKET') {
      if (!scheduledAt) {
        onError(t('common.studio.live.startParamsTicketScheduleRequired'));
        return null;
      }
      const fee = Math.max(1, Math.floor(Number(entryFeeCoins || pricing?.ticketPriceCoins || 100)));
      const capacityRaw = ticketCapacity.trim();
      const capacity =
        capacityRaw.length === 0 ? null : Math.max(1, Math.floor(Number(capacityRaw)));
      if (capacityRaw.length > 0 && !Number.isFinite(capacity)) {
        onError(t('common.studio.live.startParamsTicketCapacityInvalid'));
        return null;
      }
      return {
        toType,
        scheduledAt: new Date(scheduledAt).toISOString(),
        durationMinutes,
        entryFeeCoins: fee,
        ticketCapacity: capacity,
      };
    }

    if (toType === 'PARTY') {
      const min = Math.max(2, Math.floor(Number(minParticipants || pricing?.partyMinParticipants || 2)));
      const fee = Math.max(1, Math.floor(Number(entryFeeCoins || pricing?.partyEntryCoins || 50)));
      return {
        toType,
        minParticipants: min,
        entryFeeCoins: fee,
      };
    }

    if (toType === 'PEEP') {
      const rate = Math.max(
        1,
        Math.floor(
          Number(
            coinsPerMinute ||
              Math.floor(
                ((pricing?.vipCoinsPerMinute ?? 80) * (pricing?.spyDiscountPercent ?? 30)) / 100,
              ),
          ),
        ),
      );
      return { toType, coinsPerMinute: rate };
    }

    return { toType };
  }

  async function handleTransition(toType: ShowType): Promise<void> {
    const body = buildTransitionBody(toType);
    if (!body) {
      return;
    }

    try {
      const result = await api.transitionShow(liveShow.id, body);
      onShowUpdated(result.show);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not change show type.');
    }
  }

  async function handleStartScheduled(): Promise<void> {
    try {
      const result = await api.startScheduledShow(liveShow.id);
      onShowUpdated(result.show);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not start show.');
    }
  }

  const transitionTargets = listModelTransitionTargets(liveShow.type);
  const transitionTypes = showTypes.filter((type) => {
    if (!transitionTargets.includes(type.type)) {
      return false;
    }
    if (type.type === 'PARTY' && pricing?.partyEnabled === false) {
      return false;
    }
    return true;
  });

  return (
    <>
      {liveShow.status === 'WAITING' && liveShow.type === 'TICKET' ? (
        <div className="fm-card" style={{ marginTop: '1rem' }}>
          <p style={{ margin: 0, color: 'var(--fm-color-text-muted)' }}>
            {liveShow.access.scheduledAt
              ? t('common.show.scheduledStartsAt').replace(
                  '{{datetime}}',
                  new Date(liveShow.access.scheduledAt).toLocaleString(),
                )
              : t('common.studio.live.scheduleShowHint')}
          </p>
          <p style={{ margin: '0.5rem 0 0', color: 'var(--fm-color-link)' }}>
            {t('common.home.ticketsSold')
              .replace('{{sold}}', String(liveShow.access.ticketsSold))
              .replace(
                '{{capacity}}',
                liveShow.access.ticketCapacity ? String(liveShow.access.ticketCapacity) : '∞',
              )}
          </p>
          {liveShow.access.durationMinutes ? (
            <p style={{ margin: '0.35rem 0 0', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
              {t('common.studio.live.startParamsDurationOption').replace(
                '{{minutes}}',
                String(liveShow.access.durationMinutes),
              )}
            </p>
          ) : null}
          <div style={{ marginTop: '1rem' }}>
            <Button
              label={t('common.studio.live.startScheduledEarly')}
              disabled={isSubmitting}
              onClick={() => {
                void handleStartScheduled();
              }}
            />
          </div>
        </div>
      ) : null}

      {isPublicMainStage(liveShow.type) && liveShow.status === 'LIVE' && transitionTypes.length > 0 ? (
        <div className="fm-card" style={{ marginTop: '1rem' }}>
          <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.live.transitionTitle')}</h2>
          <p style={{ margin: '0 0 0.75rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
            {t('common.studio.live.transitionHint')}
          </p>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {transitionTypes.map((type) => (
              <div
                key={type.type}
                className="fm-card"
                style={{ marginTop: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
              >
                <div>
                  <strong>{type.label}</strong>
                  <p style={{ margin: '0.35rem 0 0', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
                    {type.description}
                  </p>
                </div>

                {type.type === 'TICKET' ? (
                  <div style={{ display: 'grid', gap: '0.55rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem' }}>
                      {t('common.studio.live.scheduleShowAt')}
                      <input
                        className="fm-input"
                        type="datetime-local"
                        value={scheduledAt}
                        onChange={(event) => {
                          setScheduledAt(event.target.value);
                        }}
                      />
                    </label>
                    <label style={{ display: 'block', fontSize: '0.875rem' }}>
                      {t('common.studio.live.startParamsDuration')}
                      <select
                        className="fm-input"
                        value={String(durationMinutes)}
                        onChange={(event) => {
                          setDurationMinutes(Number(event.target.value));
                        }}
                      >
                        {TICKET_DURATION_OPTIONS.map((minutes) => (
                          <option key={minutes} value={minutes}>
                            {t('common.studio.live.startParamsDurationOption').replace(
                              '{{minutes}}',
                              String(minutes),
                            )}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: 'block', fontSize: '0.875rem' }}>
                      {t('common.studio.live.startParamsTicketPrice')}
                      <input
                        className="fm-input"
                        type="number"
                        min={1}
                        value={entryFeeCoins || String(pricing?.ticketPriceCoins ?? 100)}
                        onChange={(event) => {
                          setEntryFeeCoins(event.target.value);
                        }}
                      />
                    </label>
                    <label style={{ display: 'block', fontSize: '0.875rem' }}>
                      {t('common.studio.live.startParamsTicketCapacity')}
                      <input
                        className="fm-input"
                        type="number"
                        min={1}
                        placeholder={t('common.studio.live.startParamsTicketCapacityUnlimited')}
                        value={ticketCapacity}
                        onChange={(event) => {
                          setTicketCapacity(event.target.value);
                        }}
                      />
                    </label>
                  </div>
                ) : null}

                {type.type === 'PARTY' ? (
                  <div style={{ display: 'grid', gap: '0.55rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem' }}>
                      {t('common.studio.live.startParamsPartyEntry')}
                      <input
                        className="fm-input"
                        type="number"
                        min={1}
                        value={entryFeeCoins || String(pricing?.partyEntryCoins ?? 50)}
                        onChange={(event) => {
                          setEntryFeeCoins(event.target.value);
                        }}
                      />
                    </label>
                    <label style={{ display: 'block', fontSize: '0.875rem' }}>
                      {t('common.studio.live.startParamsPartyMin')}
                      <input
                        className="fm-input"
                        type="number"
                        min={2}
                        value={minParticipants || String(pricing?.partyMinParticipants ?? 2)}
                        onChange={(event) => {
                          setMinParticipants(event.target.value);
                        }}
                      />
                    </label>
                  </div>
                ) : null}

                {type.type === 'PEEP' ? (
                  <label style={{ display: 'block', fontSize: '0.875rem' }}>
                    {t('common.studio.live.startParamsPeepRate')}
                    <input
                      className="fm-input"
                      type="number"
                      min={1}
                      value={coinsPerMinute}
                      onChange={(event) => {
                        setCoinsPerMinute(event.target.value);
                      }}
                    />
                  </label>
                ) : null}

                <Button
                  label={t('common.studio.live.transitionStart').replace('{{type}}', type.label)}
                  variant="secondary"
                  disabled={isSubmitting}
                  onClick={() => {
                    void handleTransition(type.type);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}
